import { useAuth, useUser } from '@clerk/clerk-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { PAYMENT_SETUP_ENABLED } from '../../../utils/featureFlags';
import { Logout } from '@mui/icons-material';
import { CallWithAuth } from '../../../actions/apiAction';
import { CREATOR_BANK_ACCOUNT } from '../../../utils/apiUrl';
import { getEarningsMetrics } from '../../../utils/earningsCache';
import { warmSubscribersMetrics } from '../../../utils/subscribersCache';
import { hardSignOut } from '../../../services/authSession';
import useAuthContext from '../../../hooks/useAuth';
import Logo from '../../../assets/images/logo.png';

export default function Dashboard() {
	const navigate = useNavigate();
	const { signOut } = useAuth();
	const { user: clerkUser } = useUser();
	const { user: creatorUser } = useAuthContext();
	const [bankStatus, setBankStatus] = useState(null);
	const [statusLoading, setStatusLoading] = useState(true);
	// Subscription money already collected but locked until payout setup is done.
	const [heldBalance, setHeldBalance] = useState(0);

	// Prefer creator CDN profile pic from /users/me; fall back to Clerk avatar.
	const avatarUrl =
		creatorUser?.profile_picture_url || clerkUser?.imageUrl || null;
	const displayName =
		clerkUser?.firstName || creatorUser?.first_name || 'there';
	const avatarInitial =
		(displayName && displayName !== 'there'
			? displayName.charAt(0)
			: creatorUser?.username?.charAt(0)) || '?';

	const loadBankStatus = useCallback(async () => {
		if (!PAYMENT_SETUP_ENABLED) {
			setStatusLoading(false);
			return;
		}
		setStatusLoading(true);
		try {
			const res = await CallWithAuth('GET', CREATOR_BANK_ACCOUNT);
			if (res?.data) setBankStatus(res.data);
		} catch (err) {
			console.error('Failed to load bank account status', err);
		} finally {
			setStatusLoading(false);
		}
	}, []);

	useEffect(() => {
		loadBankStatus();
		warmSubscribersMetrics();
		// Same prefetch as warmEarningsMetrics, but keep the result so the setup
		// reminder can name the amount the creator is leaving on the table.
		getEarningsMetrics()
			.then((data) => setHeldBalance(Number(data?.held_balance) || 0))
			.catch((err) => console.warn('Earnings prefetch failed', err));
	}, [loadBankStatus]);

	const needsPaymentSetup =
		PAYMENT_SETUP_ENABLED &&
		!statusLoading && bankStatus && !bankStatus.payout_ready;


	return (
		<>
			<Helmet>
				<title> Dashboard</title>
			</Helmet>
			{/* start: dashboard page */}
			<div className="main-body">
				<div className="body-content">
					<div className="main-header-s1">
						{/* <h1 className="app-title"> */}
						<img src={Logo}
							style={{ width: '70px', height: '70px' }}
							alt="TLK" />
						{/* <sub className="app-title__sub">creators</sub> */}
						{/* </h1> */}
					</div>
					<h1 className="main-header-s1__title" style={{
						justifyContent: 'center',
						textAlign: 'center',
						padding: '0 0 20px 0'
					}}>Creator Dashboard</h1>

					<div className="dashboard-section">
						<div className="user-information-card">
							<div className="top-info">
								<figure>
									<button
										type="button"
										className="image-container"
										onClick={() => navigate('/user/my-profile')}
										aria-label="Open account settings"
										style={{
											padding: 0,
											border: '2px solid var(--white)',
											background: 'transparent',
											cursor: 'pointer',
										}}
									>
										{avatarUrl ? (
											<img
												src={avatarUrl}
												alt={displayName}
												referrerPolicy="no-referrer"
											/>
										) : (
											<span
												aria-hidden="true"
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													width: '100%',
													height: '100%',
													borderRadius: '100%',
													background:
														'linear-gradient(180deg, #8F40D3 0%, #4B73EB 100%)',
													color: '#fff',
													fontWeight: 600,
													fontSize: 28,
													textTransform: 'uppercase',
												}}
											>
												{avatarInitial}
											</span>
										)}
									</button>
								</figure>
								<h2>{creatorUser?.first_name || displayName} {creatorUser?.last_name ?? ""}</h2>
								<button
									type="button"
									className="logout-btn"
									onClick={() => hardSignOut(signOut)}
								>
									<Logout sx={{ fontSize: 16 }} />
									<span>Logout</span>
								</button>
							</div>
						</div>
						{/* {needsPaymentSetup && (
							<button
								type="button"
								onClick={() => navigate('/user/payment-setup')}
								className="w-full rounded-xl px-3 py-2.5 border mb-3 text-left"
								style={{
									background: 'rgba(236, 63, 160, 0.08)',
									borderColor: 'rgba(236, 63, 160, 0.22)',
									cursor: 'pointer',
								}}
							>
								<div className="flex items-start gap-2.5">
									<div
										className="rounded-full flex-shrink-0 flex items-center justify-center"
										style={{
											width: 28,
											height: 28,
											background: '#1c1917',
											boxShadow:
												'0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(236, 63, 160, 0.35)',
										}}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width={14}
											height={14}
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												fill="#f9a8d4"
												d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"
											/>
										</svg>
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-xs font-semibold mb-0.5 text-foreground leading-snug">
											Finish setting up your account
										</p>
										<p className="text-[11px] leading-snug text-muted-foreground mb-0">
											Complete payment setup so you can get paid for subscriptions.{' '}
											<span className="underline text-foreground font-medium">
												Continue setup
											</span>
										</p>
									</div>
								</div>
							</button>
						)} */}
						<div className="dashboard-menu-xl-lists">
							{needsPaymentSetup && (
								<button
									type="button"
									className="dashboard-setup-reminder"
									onClick={() => navigate('/user/payment-setup')}
								>
									<div className="dashboard-setup-reminder__icon" aria-hidden="true">
										<svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24">
											<path
												fill="#facc15"
												d="M12 2.5c-.5 0-.95.27-1.2.7L1.9 18.3c-.5.9.15 2 1.2 2h17.8c1.05 0 1.7-1.1 1.2-2L13.2 3.2c-.25-.43-.7-.7-1.2-.7z"
											/>
											<path fill="#1c1917" d="M11.25 9.25h1.5v5.5h-1.5z" />
											<circle cx="12" cy="17.25" r="1" fill="#1c1917" />
										</svg>
									</div>
									<div className="dashboard-setup-reminder__body">
										<p className="dashboard-setup-reminder__title">
											{heldBalance > 0
												? `$${heldBalance.toFixed(2)} waiting for you`
												: 'Finish setting up your account'}
										</p>
										<p className="dashboard-setup-reminder__text">
											{heldBalance > 0
												? "You've already earned this from subscriptions. Complete payment setup to unlock it."
												: 'Complete payment setup so you can get paid for subscriptions.'}{' '}
											<span className="dashboard-setup-reminder__cta">Continue setup</span>
										</p>
									</div>
								</button>
							)}
							<div
								className="dashboard-menu-item"
								onClick={() => navigate('/user/my-profile')}
							>
								<div
									className="icon-container"
									style={{
										background: 'linear-gradient(180deg, #6B7280 0%, #4B5563 100%)',
									}}
								>
									<img
										src="/images/icons/account-setting-icon.svg"
										alt=""
										style={{ width: 20, height: 20 }}
									/>
								</div>
								<div className="text-container">
									<h2>Account settings</h2>
									<p>Manage your Profile</p>
								</div>
								<div className="arow">
									<img src="/images/icons/chevron-right.svg" alt="" />
								</div>
							</div>
							<div className="dashboard-menu-item" onClick={() => navigate('/user/my-subscribers')}>
								<div
									className="icon-container"
									style={{
										background: 'linear-gradient(180deg, #8F40D3 0%, #7B2CBF 100%)',
									}}
								>
									<span className="icon-Subscribers" />
								</div>
								<div className="text-container">
									<h2>My Subscribers</h2>
									<p>Track and connect with your fans</p>
								</div>
								<div className="arow">
									<img src="/images/icons/chevron-right.svg" alt="" />
								</div>
							</div>
							<div
								className="dashboard-menu-item"
								onClick={() => navigate('/user/my-guardrails')}
							>
								<div
									className="icon-container"
									style={{
										background: 'linear-gradient(180deg, #4B73EB 0%, #375FD7 100%)',
									}}
								>
									<span className="icon-Guardrails" />
								</div>
								<div className="text-container">
									<h2>My Guardrails</h2>
									<p>Set limits on your digital twin.</p>
								</div>
								<div className="arow">
									<img src="/images/icons/chevron-right.svg" alt="" />
								</div>
							</div>
							<div className="dashboard-menu-item" onClick={() => navigate('/user/conversation')}>
								<div
									className="icon-container"
									style={{
										background: 'linear-gradient(180deg, #EBB738 0%, #D7A324 100%)',
									}}
								>
									<span className="icon-messages" />
								</div>
								<div className="text-container">
									<h2>My conversations</h2>
									<p>
										See what fans are talking about with your twin
									</p>
								</div>
								<div className="arow">
									<img src="/images/icons/chevron-right.svg" alt="" />
								</div>
							</div>
							<div
								className="dashboard-menu-item"
								onClick={() => navigate('/user/my-earnings')}
							>
								<div
									className="icon-container"
									style={{
										background: 'linear-gradient(180deg, #5AD282 0%, #46BE6E 100%)',
									}}
								>
									<span className="icon-Earnings" />
								</div>
								<div className="text-container">
									<h2>Earnings Overview</h2>
									<p>Monitor your earnings over time</p>
								</div>
								<div className="arow">
									<img src="/images/icons/chevron-right.svg" alt="" />
								</div>
							</div>
							{PAYMENT_SETUP_ENABLED && (
								<div
									className="dashboard-menu-item"
									onClick={() => navigate('/user/payment-setup')}
								>
									<div
										className="icon-container"
										style={{
											background: 'linear-gradient(180deg, #EC3FA0 0%, #B23CE0 100%)',
										}}
									>
										<span className="icon-cards" />
									</div>
									<div className="text-container">
										<h2>Payment Setup</h2>
										<p>
											{needsPaymentSetup
												? 'Add a bank account to get paid for subscriptions.'
												: 'Manage the bank account we pay out to.'}
										</p>
									</div>
									<div className="arow">
										<img src="/images/icons/chevron-right.svg" alt="" />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{/* end: dashboard page */}
		</>
	);
}
